// Copyright 2021 Northern.tech AS
//
//    Licensed under the Apache License, Version 2.0 (the "License");
//    you may not use this file except in compliance with the License.
//    You may obtain a copy of the License at
//
//        http://www.apache.org/licenses/LICENSE-2.0
//
//    Unless required by applicable law or agreed to in writing, software
//    distributed under the License is distributed on an "AS IS" BASIS,
//    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//    See the License for the specific language governing permissions and
//    limitations under the License.
import { Fragment, useEffect, useMemo, useState } from 'react';

// material ui
import { Alert, Button, Chip, FormControl, FormHelperText, TextField, Typography, textFieldClasses } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import BaseDrawer from '@northern.tech/common-ui/BaseDrawer';
import { ConfirmModal } from '@northern.tech/common-ui/ConfirmModal';
import { ContentSection } from '@northern.tech/common-ui/ContentSection';
import { CopyTextToClipboard } from '@northern.tech/common-ui/CopyText';
import { ColumnWidthProvider, SynchronizedTwoColumnData, TwoColumnData } from '@northern.tech/common-ui/TwoColumnData';
import actions from '@northern.tech/store/actions';
import { rolesByName, twoFAStates, uiPermissionsByArea, uiPermissionsById } from '@northern.tech/store/constants';
import { useAppDispatch } from '@northern.tech/store/store';
import { passwordResetStart } from '@northern.tech/store/thunks';
import { mapUserRolesToUiPermissions } from '@northern.tech/store/utils';
import type { User } from '@northern.tech/types/MenderTypes';
import { isEmpty } from '@northern.tech/utils/helpers';

import { OAuth2Providers, genericProvider } from '../../login/OAuth2Providers';
import { EmailVerificationWarning } from '../EmailVerificationWarning';
import { SETTINGS_FORM_MAX_WIDTH, SETTINGS_INPUT_WIDTH, SETTINGS_INPUT_WIDTH_ROLES_AND_USERS_ONLY } from '../constants';
import { UserRolesSelect } from './UserForm';

const { setSnackbar } = actions;

const useStyles = makeStyles()(theme => ({
  oauthIcon: { fontSize: 36, marginRight: 10 },
  userIdWrapper: {
    '.copy-button': { marginTop: theme.spacing(0.25), whiteSpace: 'nowrap' },
    [`&.profile-settings .${textFieldClasses.root}`]: { minWidth: SETTINGS_INPUT_WIDTH },
    maxWidth: SETTINGS_INPUT_WIDTH_ROLES_AND_USERS_ONLY
  },
  widthLimit: { maxWidth: SETTINGS_FORM_MAX_WIDTH, [`.${textFieldClasses.root}`]: { width: SETTINGS_INPUT_WIDTH_ROLES_AND_USERS_ONLY } }
}));

export const getUserSSOState = user => {
  const { sso = [] } = user;
  const isOAuth2 = !!sso.length;
  let provider = null;
  if (isOAuth2) {
    provider = OAuth2Providers.find(provider => sso.some(({ kind }) => kind.includes(provider.id))) ?? genericProvider;
  }
  return { isOAuth2, provider };
};

const mapPermissions = permissions => permissions.map(permission => uiPermissionsById[permission].title).join(', ') || 'None';

const scopedPermissionAreas = {
  groups: 'Device groups',
  releases: 'Releases'
};

export const UserId = ({ className = '', userId }) => {
  const { classes } = useStyles();
  return (
    <div className={`flexbox margin-bottom-small ${classes.userIdWrapper} ${className}`}>
      <TextField className="margin-right-small" label="User ID" key={userId} disabled defaultValue={userId} />
      <div className="copy-button">
        <CopyTextToClipboard notify={false} token={userId} />
      </div>
    </div>
  );
};

interface UserDefinitionProps {
  currentUser: User & { verified?: boolean };
  hasMultitenancy: boolean;
  isEnterprise: boolean;
  onCancel: () => void;
  onRemove: (user: User) => void;
  onSubmit: (userData: (User & { roles?: string[] }) | null, type: string, id: string) => void;
  roles: { name: string; value?: string }[];
  selectedUser: User & { roles?: string[] };
}

const authChipProps = {
  size: 'small',
  variant: 'outlined',
  color: 'warning'
};

export const UserDefinition = ({ currentUser, hasMultitenancy, isEnterprise, onCancel, onSubmit, onRemove, roles, selectedUser = {} }: UserDefinitionProps) => {
  const { email = '', id } = selectedUser;

  const { classes } = useStyles();
  const dispatch = useAppDispatch();

  const [currentEmail, setCurrentEmail] = useState('');
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [hadRoleChanges, setHadRoleChanges] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingRoles, setIsEditingRoles] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const rolesById = useMemo(
    () => roles.reduce((accu, role) => ({ ...accu, [role.value ?? role.name]: { ...role, value: role.value ?? role.name } }), {}),
    [roles]
  );

  useEffect(() => {
    setCurrentEmail(email);
    setEmailInvalid(false);
    setIsEditingEmail(false);
  }, [email]);

  useEffect(() => {
    setSelectedRoles(selectedUser.roles || []);
    setHadRoleChanges(false);
    setIsEditingRoles(false);
  }, [selectedUser.roles]);

  const validateEmailChange = ({ target: { value } }) => {
    setEmailInvalid(!validator.isEmail(value) || validator.isEmpty(value));
    setCurrentEmail(value);
  };

  const onCancelEmailChanges = () => {
    setCurrentEmail(email);
    setEmailInvalid(false);
    setIsEditingEmail(false);
  };

  const onEmailSubmitClick = () => onSubmit({ ...selectedUser, email: currentEmail }, 'edit', id);

  const onRemoveClick = () => {
    onRemove(selectedUser);
  };

  const onRolesSelect = (newlySelectedRoles, hadRoleChanges) => {
    setSelectedRoles(newlySelectedRoles);
    setHadRoleChanges(hadRoleChanges);
  };

  const onCancelRoleChanges = () => {
    setSelectedRoles(selectedUser.roles || []);
    setHadRoleChanges(false);
    setIsEditingRoles(false);
  };

  const onPasswordResetConfirmed = () =>
    dispatch(passwordResetStart(email))
      .unwrap()
      .then(() => dispatch(setSnackbar(`A password reset email was sent to ${email}.`)));

  const onSubmitClick = () => {
    onSubmit({ ...selectedUser, roles: selectedRoles }, 'edit', id);
    setIsEditingRoles(false);
  };

  const { areas, ...scopedAreas } = useMemo(() => {
    const emptySelection = { areas: {}, groups: {}, releases: {} };
    if (!selectedRoles.length || isEmpty(rolesById)) {
      return emptySelection;
    }

    return Object.entries(mapUserRolesToUiPermissions(selectedRoles, rolesById)).reduce((accu, [key, values]) => {
      if (scopedPermissionAreas[key]) {
        accu[key] = Object.entries(values).reduce((groupsAccu, [name, uiPermissions]) => {
          groupsAccu[name] = mapPermissions(uiPermissions);
          return groupsAccu;
        }, {});
      } else {
        accu.areas[uiPermissionsByArea[key].title] = mapPermissions(values);
      }
      return accu;
    }, emptySelection);
  }, [selectedRoles, rolesById]);

  const hasScopedPermissionsDefined = Object.values(scopedAreas).some(permissions => !isEmpty(permissions));
  const userNotVerified = !currentUser.verified;
  const isSubmitDisabled = !selectedRoles.length || !hadRoleChanges;

  const { isOAuth2, provider } = getUserSSOState(selectedUser);
  const rolesClasses = isEnterprise ? '' : 'muted';

  const verificationChip = selectedUser.verified ? (
    <Chip {...authChipProps} label="Verified" color="success" />
  ) : (
    <Chip {...authChipProps} label="Not verified" />
  );

  const tfaStatus = isOAuth2 ? (
    'SSO managed'
  ) : selectedUser.tfa_status === twoFAStates.enabled ? (
    <Chip {...authChipProps} label="Enabled" color="success" />
  ) : (
    <Chip {...authChipProps} label="Not enabled" />
  );

  const signInData = {
    Email: (
      <>
        {email} {verificationChip}
      </>
    ),
    'User ID': id,
    'Two-factor authentication': tfaStatus
  };

  return (
    <BaseDrawer onClose={onCancel} open={!!id} size="md" slotProps={{ header: { title: `User information for ${email}` } }}>
      {hasMultitenancy && userNotVerified && <EmailVerificationWarning className="margin-top-small" action="change another user’s email" />}
      <ContentSection title="Sign-in & security">
        <TwoColumnData data={hasMultitenancy ? signInData : { 'User ID': id }} setSnackbar={setSnackbar} />

        {!hasMultitenancy && (
          <>
            <FormControl className={`margin-top-small ${classes.widthLimit}`}>
              <TextField label="Email" id="email" value={currentEmail} disabled={!isEditingEmail} error={emailInvalid} onChange={validateEmailChange} />
              {emailInvalid && <FormHelperText className="warning">Please enter a valid email address</FormHelperText>}
            </FormControl>
            {currentUser.id !== id && (
              <div className="flexbox margin-top-small">
                {isEditingEmail ? (
                  <>
                    <Button color="info" variant="outlined" className="margin-right-small" onClick={onCancelEmailChanges}>
                      Cancel
                    </Button>
                    <Button variant="contained" disabled={emailInvalid || currentEmail === email} onClick={onEmailSubmitClick}>
                      Save changes
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => setIsEditingEmail(true)}>Change email</Button>
                )}
              </div>
            )}
          </>
        )}
        {hasMultitenancy && !isOAuth2 && (
          <>
            <div>
              <Button className="margin-top-small" onClick={() => setShowResetConfirmation(true)}>
                Send password reset link
              </Button>
            </div>
            <ConfirmModal
              header="Send password reset link?"
              description={
                <>
                  We&rsquo;ll send an email to <b>{email}</b> with instructions for resetting their Mender account password.
                </>
              }
              isDanger={false}
              open={showResetConfirmation}
              close={() => setShowResetConfirmation(false)}
              onConfirm={onPasswordResetConfirmed}
            />
          </>
        )}
        {isOAuth2 && (
          <div className="flexbox margin-top-small margin-bottom">
            <div className={classes.oauthIcon}>{provider.icon}</div>
            <div className="info">
              This user logs in using their <strong>{provider.name}</strong> account.
              <br />
              They can connect to {provider.name} to update their login settings.
            </div>
          </div>
        )}
      </ContentSection>
      <ContentSection title="Roles">
        <UserRolesSelect
          key={`roles-select-${isEditingRoles}`}
          disabled={!isEnterprise || !isEditingRoles}
          currentUser={currentUser}
          onSelect={onRolesSelect}
          roles={roles}
          user={selectedUser}
        />
        {isEnterprise && (
          <div className="flexbox margin-top-small">
            {isEditingRoles ? (
              <>
                <Button color="info" variant="outlined" className="margin-right-small" onClick={onCancelRoleChanges}>
                  Cancel
                </Button>
                <Button variant="contained" disabled={isSubmitDisabled} onClick={onSubmitClick} color="secondary">
                  Save changes
                </Button>
              </>
            ) : (
              <Button color="secondary" onClick={() => setIsEditingRoles(true)}>
                Change roles
              </Button>
            )}
          </div>
        )}
        {!isEnterprise && (
          <Alert className={`margin-top-small ${classes.widthLimit}`} severity="warning">
            Role-base access control (RBAC) is not available in your current plan. All users will have full administrative access
            {selectedRoles.includes(rolesByName.admin) ? ', and the permissions shown below apply to all users' : ''}.
          </Alert>
        )}
        <ColumnWidthProvider>
          {!!(hasScopedPermissionsDefined || !isEmpty(areas)) && (
            <Typography className="margin-top margin-bottom-small" variant="subtitle1">
              Role permissions
            </Typography>
          )}
          <SynchronizedTwoColumnData className={rolesClasses} data={areas} />
          {Object.entries(scopedAreas)
            .filter(([, areaPermissions]) => !isEmpty(areaPermissions))
            .map(([area, areaPermissions]) => (
              <Fragment key={area}>
                <Typography className="margin-top margin-bottom-x-small" variant="subtitle1">
                  {scopedPermissionAreas[area]}
                </Typography>
                <SynchronizedTwoColumnData className={rolesClasses} data={areaPermissions} />
              </Fragment>
            ))}
        </ColumnWidthProvider>
      </ContentSection>
      <div className="flexbox margin-top-small">
        {currentUser.id !== id && (
          <Button color="error" onClick={onRemoveClick} variant="outlined">
            Delete user
          </Button>
        )}
      </div>
    </BaseDrawer>
  );
};

export default UserDefinition;
